import type { ToolDefinition, ToolType } from "../types";

const PDF_TO_IMAGE_TOOL_TYPE = {
  label: "Free PDF Converter",
  icon: "FileImage",
} as const satisfies ToolType;

const pdfToImageTool: ToolDefinition = {
  slug: "pdf-to-image",
  name: "Convert PDF to JPG & PNG Free | High Quality PDF to Image",
  title: "PDF to Image Converter",
  description:
    "Free online tool to save PDF as images. Convert your document to high-quality PNG or JPG files, split pages into separate JPEGs, and download as a ZIP.",
  longDescription:
    "Easily convert any PDF document into high-resolution images right in your browser. Extract pages as crisp PNGs or compressed JPGs without losing quality, and download all pages instantly in a convenient ZIP file.",
  toolType: {
    label: "PDF Converter",
    icon: "FileImage",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "High-Resolution Output",
    "ZIP File Delivery",
  ],
  category: "PDF Tools",
  categorySlug: "pdf-tools",
  href: "/tools/pdf-to-image",
  api: "/api/v1/pdf/pdf-to-images",
  keywords: [
    "snappy pdf to image tool",
    "convert document to high quality png",
    "how to save pdf as photo",
    "split pages into separate jpegs",
    "change pdf to jpg pictures",
    "best online converter app",
    "make images without losing quality",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Save a PDF as a Picture",
        description:
          "Follow these simple steps to convert your PDF document into high-quality image files quickly and securely.",
      },
      benefits: {
        title: "Why Use Our PDF to Image Converter?",
        description:
          "Discover the advantages of our streamlined, high-resolution rendering engine for all your document conversion needs.",
      },
      faqs: {
        title: "Frequently Asked Questions About PDF Conversion",
        description:
          "Find answers on how to get the best quality out of your PDF to JPG or PNG conversions.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "Change PDF to Photo Without Losing Quality",
        level: "h2",
        paragraphs: [
          "Whether you need to share a document on social media, insert a presentation slide into an email, or upload a resume to a web form that only accepts photos, figuring out how to save a PDF as a picture can be a frustrating roadblock. Standard screen captures often result in blurry text and pixelated graphics, completely ruining the professionalism of your original document. Our free snappy pdf to image tool solves this problem by directly rendering the vector data of your PDF into crisp, pixel-perfect raster images at 2× resolution. This ensures that even the smallest text remains completely legible and graphic elements retain their sharp edges.",
          "When you need to split pages into separate JPEGs or extract a massive multi-page manual into individual images, doing it manually is incredibly time-consuming. Our converter automates the entire process. By simply uploading your file, the system will mass convert PDF to JPG or PNG formats, processing every single page individually. To make organizing your new files effortless, the tool automatically bundles all the generated images into a single, structured ZIP archive. This means you can convert a 50-page document into 50 high-quality images in seconds, completely free, and without ever having to install complex desktop software or create an account.",
        ],
        note: "All processing happens securely, ensuring your documents remain private.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Key Conversion Features",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "High-Resolution Output",
                icon: "Monitor",
                description:
                  "We render your PDFs at 2× default resolution to guarantee that you can convert a document to high quality PNG or JPG format without the typical blurriness.",
                accent: "primary",
              },
              {
                label: "Instant ZIP Download",
                icon: "FileArchive",
                description:
                  "Instead of clicking download on 20 different pages, our tool packages your entire multi-page PDF into one easy-to-download ZIP file containing all your images.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Choosing the Right Image Format",
            description:
              "Understand the differences between the available output formats to ensure you get the best result for your specific use case.",
            columns: [
              "Output Format",
              "Best Used For",
              "File Size",
              "Quality Characteristics",
            ],
            rows: [
              {
                "Output Format": "JPG / JPEG",
                "Best Used For":
                  "Sharing on mobile, embedding in emails, general web use",
                "File Size": "Smallest (Highly Compressed)",
                "Quality Characteristics":
                  "Good quality, but may introduce slight artifacts around fine text",
              },
              {
                "Output Format": "PNG",
                "Best Used For":
                  "Printing, professional graphics, documents with tiny text",
                "File Size": "Larger (Lossless Compression)",
                "Quality Characteristics":
                  "Perfectly sharp text and graphics, no compression artifacts",
              },
              {
                "Output Format": "PPM",
                "Best Used For":
                  "Legacy Unix systems, specific programmatic workflows",
                "File Size": "Massive (Uncompressed)",
                "Quality Characteristics":
                  "Raw pixel data, exact color representation",
              },
            ],
            note: "For 95% of users, PNG is recommended for text-heavy documents, while JPG is best for scanned photos and sharing.",
            emphasisColumns: ["Best Used For"],
            monospaceColumns: ["Output Format"],
          },
        ],
      },
    ],
    faqs: [
      {
        question: "How do I save every page of a PDF as a separate image?",
        answer:
          "To save every page of a PDF as a separate image, simply upload your document to our online converter. The tool automatically detects the number of pages in your file and renders each one individually. Once the conversion is complete, the tool bundles all the separate JPEG or PNG images into a single ZIP file. You just download the ZIP, extract it, and you will have a folder containing every page formatted as an individual high-quality image file.",
      },
      {
        question:
          "What is the best way to convert PDF to JPG without losing quality?",
        answer:
          "The best way to change PDF to JPG pictures without losing quality is to use a converter that renders the file at a high DPI (Dots Per Inch) or resolution multiplier. Standard converters often render at screen resolution (72 DPI), which makes text blurry. Our tool renders the PDF vector data at 2× resolution, effectively doubling the pixel density before saving it as a JPG. This ensures that your text and charts remain crisp and readable even after the JPEG compression is applied.",
      },
      {
        question:
          "What is the difference between PNG and JPG when converting from PDF?",
        answer:
          "When you turn PDF into JPG, the image undergoes 'lossy' compression, which shrinks the file size dramatically but can sometimes leave fuzzy artifacts around sharp edges like text and logos. JPG is ideal for sharing via email or messaging apps where file size matters. When you convert document to high quality PNG, the process uses 'lossless' compression. The file size will be larger, but the lines, text, and flat colors will be absolutely pristine, making PNG the best choice for professional printing or detailed graphic design work.",
      },
      {
        question:
          "Is it possible to convert a PDF to a PNG on mobile without an app?",
        answer:
          "Yes, absolutely. Our best online converter app is completely web-based and fully optimized for mobile browsers on both iOS and Android. You do not need to download anything from the App Store or Google Play. Just open your mobile browser, navigate to our tool, tap to upload your PDF from your phone's file system, and choose PNG. The tool will process the file on our secure servers and provide a ZIP file containing your images that you can save directly to your mobile device.",
      },
      {
        question:
          "How to download all PDF pages as images in a single ZIP file?",
        answer:
          "Downloading your converted pages as a ZIP is the default behavior of our tool. After you upload the PDF and click convert, the backend engine processes all selected pages. Once finished, the interface will present a 'Download' button. Clicking this button will automatically save a file named `pdf_pages.zip` to your computer or device. Inside this archive, you will find all your pages neatly numbered and converted to your requested image format.",
      },
      {
        question: "Why does my PDF look pixelated when I save it as a JPEG?",
        answer:
          "If you are using built-in OS tools or low-quality online converters, they often take a basic snapshot of the PDF at a very low resolution to save processing power. This results in severe pixelation, especially on text. To fix a blurry PDF to image conversion, you must use a tool that utilizes a high-resolution rendering engine. Our tool processes the internal structure of the PDF and scales it up to 2× standard resolution before rasterizing it, ensuring you make images without losing quality.",
      },
    ],
    benefits: [
      {
        icon: "ImageHigh",
        title: "2× Resolution Engine",
        description:
          "We don't just take a screenshot. Our engine renders your PDF vectors at double the standard resolution to guarantee incredibly crisp, high-quality PNG and JPG outputs.",
      },
      {
        icon: "FileArchive",
        title: "Automated ZIP Delivery",
        description:
          "Never click 'download' 50 times again. Our tool automatically packages every converted page into a single, organized ZIP archive for immediate download.",
      },
      {
        icon: "Settings2",
        title: "Multiple Format Support",
        description:
          "Choose exactly what you need. Easily toggle between highly compressed JPGs for quick web sharing, or lossless PNGs for perfect, print-ready text clarity.",
      },
      {
        icon: "Layers",
        title: "Batch Page Processing",
        description:
          "Upload massive documents with ease. The converter automatically reads every page of your PDF and splits them into perfectly numbered, separate image files.",
      },
      {
        icon: "Smartphone",
        title: "Mobile-Friendly Workflow",
        description:
          "Change PDF to photo directly on your iPhone or Android device. The tool runs flawlessly in mobile browsers with zero app installation required.",
      },
      {
        icon: "ShieldCheck",
        title: "Secure & Private",
        description:
          "Your business documents are safe. Files are processed securely over encrypted connections and are never stored long-term or shared with third parties.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload the PDF file",
        description:
          "Begin by dragging and dropping your PDF document into the designated upload area on the page. Alternatively, you can click the area to browse your device's file system and select the PDF you want to convert.",
      },
      {
        number: "02",
        title: "Choose PNG or JPG output",
        description:
          "Locate the format switch at the top of the tool interface. Select 'PNG' if you need lossless quality for crisp text and transparent graphics, or select 'JPG' if you need smaller file sizes for quick web sharing.",
      },
      {
        number: "03",
        title: "Convert the PDF",
        description:
          "Click the button to start the conversion process. The tool will begin rendering your document at 2× resolution, processing each page individually to ensure the highest possible quality output.",
      },
      {
        number: "04",
        title: "Download the converted image",
        description:
          "Once the processing is complete, click to save the resulting `pdf_pages.zip` file to your device. You can then extract this ZIP to access all your individual images, and use the 'Clear' button if you need to run another file.",
      },
    ],
    useCases: [
      {
        label: "Social Media Uploads",
        icon: "Share2",
        description:
          "Platforms like Instagram and Facebook do not accept PDF files. Convert your flyers and announcements into high-quality JPEGs to share them instantly.",
        accent: "primary",
      },
      {
        label: "Web Form Submissions",
        icon: "UploadCloud",
        description:
          "Many government and corporate portals only accept image files for ID and document uploads. Seamlessly convert your PDF scans into accepted PNG formats.",
        accent: "primary",
      },
      {
        label: "Presentation Design",
        icon: "Projector",
        description:
          "Extract specific pages from a PDF report as high-resolution images so you can easily embed them directly into PowerPoint or Google Slides presentations.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Students & Teachers",
        icon: "GraduationCap",
        description:
          "Perfect for extracting specific pages from digital textbooks, lecture slides, or assignments to share visually in study groups or virtual classrooms.",
        accent: "accent",
      },
      {
        label: "Graphic Designers",
        icon: "PenTool",
        description:
          "Extract client-provided PDF assets into lossless PNGs to retain perfect edge clarity and typography when importing elements into Photoshop or Illustrator.",
        accent: "accent",
      },
      {
        label: "Office Professionals",
        icon: "Briefcase",
        description:
          "Quickly turn multi-page PDF contracts, invoices, or reports into easily viewable image files for inclusion in email bodies or company chat channels.",
        accent: "accent",
      },
    ],
  },
};

export default pdfToImageTool;
export { pdfToImageTool };
